import { Outlet, ScrollRestoration } from 'react-router-dom';
import { createPortal } from 'react-dom';

import { TopBar } from '@/features/topbar';
import { useStore } from '@/legacy/store/zustand';
import { useShallow } from 'zustand/react/shallow';
import { useIsMutating } from '@tanstack/react-query';
import ModalHost from '@/legacy/components/ui/Modals/ModalHost';
import TaskTracker from '@/legacy/components/system/TaskTracker';
import { useMemo } from 'react';
import { filterNav, getMainNav } from '@/app/navigation';
import Sidebar from '@/shared/ui/sidebar/recipes/Sidebar';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useFIlterReset } from '@/shared/hooks';
import {
	useIsSuperUser,
	usePermissions,
	usePermissionItems,
	useHasPermission,
} from '@/features/access-control';
import { AddonNavItem } from '@/features/marketplace/addons';
import { MARKETPLACE_ACCESS_PERMISSIONS } from '@/features/marketplace';
import { useUserInfo } from '@/features/space-user';
import { CompanySwitch } from '@/features/auth/company-switch';
import { Overlay } from '@frekuent/orbit/components';

const AppLayout = () => {
	const { t } = useTranslation();
	const { collapsed, toggleCollapse } = useStore(
		useShallow((state) => ({
			collapsed: state.collapsed,
			toggleCollapse: state.toggleCollapse,
		})),
	);
	const { data: userInfo } = useUserInfo();
	const isDelegation = userInfo?.isDelegation;
	const isSuperUser = useIsSuperUser();
	const isLoadingLogout = useIsMutating({ mutationKey: ['auth', 'logout'] }) > 0;
	const { data: permissions, isLoading: isLoadingPermissions } = usePermissions();
	const { data: permissionItems } = usePermissionItems();
	const { hasPermission: canAccessMarketplace } = useHasPermission(
		MARKETPLACE_ACCESS_PERMISSIONS,
		'any',
	);

	useFIlterReset();

	const rolePermissions = useMemo(
		() => new Set(permissionItems?.filter((i) => i.role).map((i) => i.permission) ?? []),
		[permissionItems],
	);

	const companyPermissions = useMemo(
		() =>
			new Set(
				permissionItems?.filter((i) => i.type === 'basic' && i.company).map((i) => i.permission) ??
					[],
			),
		[permissionItems],
	);

	const navNodes = useMemo(
		() =>
			filterNav(getMainNav(t), {
				permissions,
				rolePermissions,
				companyPermissions,
				isDelegation,
				isSuperUser,
			}),
		[permissions, rolePermissions, companyPermissions, isDelegation, isSuperUser, t],
	);

	return (
		<div className='flex min-h-screen relative'>
			{isLoadingLogout && <Overlay ariaLabel={t('profile:loggingOut')} />}

			<>
				<Sidebar
					collapsed={collapsed}
					toggleCollapse={toggleCollapse}
					navNodes={navNodes}
					isLoading={isLoadingPermissions}
					slot={canAccessMarketplace ? <AddonNavItem collapsed={collapsed} /> : undefined}
					headerSlot={
						<CompanySwitch
							collapsed={collapsed}
							toggleCollapse={toggleCollapse}
						/>
					}
				/>
				<div
					className={clsx(
						'w-full',
						'flex flex-col',
						'margin-sidebar-collapsed',
						collapsed ? 'md:margin-sidebar-collapsed' : 'md:margin-sidebar-expanded',
						'main-container-query',
						'space-y-4 md:space-y-8',
					)}
				>
					<div className='page-max-width w-full page-gutter'>
						<TopBar />
					</div>
					<Outlet />
				</div>
			</>

			{/* `main-container-query` above sets `container-type`, which per spec makes that wrapper
			the containing block AND stacking context for its `position: fixed` descendants — same
			reason `StickyActionBar` portals to `document.body`. A backdrop nested inside
			that wrapper could never visually reach body-level siblings like the (also portaled)
			save-changes banner, no matter its z-index, so this portals out too. */}
			{!collapsed &&
				createPortal(
					<div
						className='fixed inset-0 z-[42] bg-black/50 md:hidden'
						onClick={toggleCollapse}
					/>,
					document.body,
				)}

			<ModalHost />
			<TaskTracker />
			<ScrollRestoration />
		</div>
	);
};

export default AppLayout;
